import { useEffect, useRef, useState } from "react";
import Button from "@/components/common/Button";
import RevealStagger from "@/components/common/RevealStagger";
import CountUp from "@/components/common/CountUp";
import SectionHead from "@/components/common/SectionHead";
import GridBackdrop from "@/components/visual/GridBackdrop";
import AccountSelector from "@/pages/landing/AccountSelector";
import { ArrowRight, ChevronRight } from "@/components/common/icons";
import { usePageMeta } from "@/lib/usePageMeta";
import { useBreadcrumbStructuredData } from "@/lib/seo";
import { env } from "@/lib/env";
import { RULES_TABLE, FAQ_ITEMS, fmtUSD } from "@/lib/data";
import { cx } from "@/lib/cx";
import { useLocale } from "@/i18n/useLocale";
import { howItWorksFor } from "@/content/how-it-works";
import type {
  ExampleStepKey,
  HiwStepKey,
  HowItWorksContent,
  ScaleRowKey,
} from "@/content/types";
import type { RuleValue } from "@/lib/data";

// A rule cell renders a locked figure or one of the two translatable words.
// Centralised so the mobile and desktop tables cannot diverge on the rule.
function ruleText(v: RuleValue, words: HowItWorksContent["rules"]["words"]): string {
  if (v.kind === "figure") return v.text;
  if (v.kind === "upTo") return `${words.upTo} ${v.figure}`;
  return v.word === "none" ? words.none : words.allowed;
}

interface StepCard {
  key: HiwStepKey;
  n: string;
}

const STEP_CARDS: readonly StepCard[] = [
  { key: "chooseAndPay", n: "01" },
  { key: "passEvaluation", n: "02" },
  { key: "tradeLive", n: "03" },
  { key: "withdrawShare", n: "04" },
];

const FAQ_BASE_URL = env.traderFaqUrl;

// FAQ deep-link contract (`<base>/<category>/<article>` href format)
// is preserved verbatim — `HowItWorks.test.tsx` test 3 continues to
// pin it.
function Hero({ t }: { t: HowItWorksContent }): JSX.Element {
  return (
    <section className="relative pt-[calc(var(--header-h,72px)+72px)] pb-12 overflow-hidden">
      <div className="brand-glow" style={{ height: 400 }} aria-hidden="true" />
      <div className="container-page relative z-[2]">
        <div className="max-w-[780px]">
          <h1
            className="font-display font-bold"
            style={{ fontSize: "clamp(40px, 4.5vw, 72px)", lineHeight: 0.96, letterSpacing: "-0.04em" }}
          >
            {t.hero.line1}
            <br />
            <span className="text-brand-gradient">{t.hero.line2Highlight}</span>
          </h1>
          <p className="text-fg-muted text-base md:text-[18px] max-w-[560px] mt-5">
            {t.hero.lede}
          </p>
        </div>
      </div>
    </section>
  );
}

// Phase HW (2026-05-09, revision 5): StepCards is a regular section
// (no sticky-pin / scroll-blocking) where the 4 step cards reveal
// cumulatively as the user scrolls past. Layout adapts by viewport:
//
//   md+ (≥ 768 px): 2-column × 4-row zigzag grid with a brand-red
//   neon line down the center gap.
//     Step 01 → row 1 col 1 (left)
//     Step 02 → row 2 col 2 (right)
//     Step 03 → row 3 col 1 (left)
//     Step 04 → row 4 col 2 (right)
//
//   mobile (< 768 px, Phase MH 2026-05-10): single-column timeline
//   with the neon line on the LEFT edge of the section. Cards stack
//   1-per-row with left padding to clear the line. Same per-card IO
//   reveal + cumulative line growth + glowing leading tip — only the
//   layout axis differs.
//
// As cards cross the IO threshold (30 % visible), each fades + slides
// in. The line height tracks revealedCount (25 % → 50 % → 75 % →
// 100 %) and the glowing tip follows the leading edge.
//
// `prefers-reduced-motion: reduce` still drops the animation entirely
// and renders all 4 cards as a flat 2-col grid (single col on mobile).

function StepCardCompact({ card, t }: { card: StepCard; t: HowItWorksContent }): JSX.Element {
  return (
    <div
      className="glass-card glass-card-hover h-full flex flex-col p-5 md:p-6 relative"
      style={{ minHeight: 200 }}
    >
      <span
        aria-hidden="true"
        className="absolute font-display font-bold leading-[0.8] tracking-tight text-brand pointer-events-none"
        style={{
          fontSize: 72,
          letterSpacing: "-0.04em",
          opacity: 0.15,
          right: 18,
          top: 14,
        }}
      >
        {card.n}
      </span>
      <div className="font-mono text-[11px] text-brand-emphasis tracking-[0.16em]">{t.steps.stepLabel} {card.n}</div>
      <h3
        className="font-display font-semibold mt-2.5"
        style={{ fontSize: "clamp(20px, 2.2vw, 26px)", letterSpacing: "-0.02em", lineHeight: 1.1 }}
      >
        {t.steps.cards[card.key].title}
      </h3>
      <p className="text-fg-muted mt-2.5 text-sm md:text-base flex-1 leading-[1.5]">{t.steps.cards[card.key].desc}</p>
      <div className="mt-4 pt-3 border-t border-border flex justify-between font-mono text-[10.5px] text-fg-dim tracking-[0.08em] uppercase">
        <span>{t.steps.cards[card.key].metaLeft}</span>
        <span className="text-brand-emphasis">{t.steps.cards[card.key].metaRight}</span>
      </div>
    </div>
  );
}

const STEP_CELLS: ReadonlyArray<{ row: number; col: number }> = [
  { row: 1, col: 1 }, // Step 01 → row 1, col 1 (left)
  { row: 2, col: 2 }, // Step 02 → row 2, col 2 (right)
  { row: 3, col: 1 }, // Step 03 → row 3, col 1 (left)
  { row: 4, col: 2 }, // Step 04 → row 4, col 2 (right)
];

const STEP_REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const STEP_MOBILE_QUERY = "(max-width: 767px)";

function StepCards({ t }: { t: HowItWorksContent }): JSX.Element {
  const [reduceMotion, setReduceMotion] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(STEP_REDUCED_MOTION_QUERY).matches;
  });
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(STEP_MOBILE_QUERY).matches;
  });
  // Number of cards revealed so far (0..4). Each card's per-element
  // IO bumps this to at least its index + 1 when the card crosses
  // 30 % visibility. Cumulative — scrolling back doesn't decrement.
  const [revealedCount, setRevealedCount] = useState(0);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const motionMql = window.matchMedia(STEP_REDUCED_MOTION_QUERY);
    const mobileMql = window.matchMedia(STEP_MOBILE_QUERY);
    const onMotion = (): void => setReduceMotion(motionMql.matches);
    const onMobile = (): void => setIsMobile(mobileMql.matches);
    motionMql.addEventListener("change", onMotion);
    mobileMql.addEventListener("change", onMobile);
    return () => {
      motionMql.removeEventListener("change", onMotion);
      mobileMql.removeEventListener("change", onMobile);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const observers: IntersectionObserver[] = [];
    cardRefs.current.forEach((node, idx) => {
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setRevealedCount((prev) =>
                idx + 1 > prev ? idx + 1 : prev,
              );
            }
          }
        },
        { threshold: 0.3 },
      );
      observer.observe(node);
      observers.push(observer);
    });
    return () => {
      for (const observer of observers) observer.disconnect();
    };
  }, [reduceMotion]);

  if (reduceMotion) {
    return (
      <section className="section-y-lg">
        <div className="container-page grid gap-4 grid-cols-1 md:grid-cols-2">
          {STEP_CARDS.map((card) => (
            <StepCardCompact key={card.key} card={card} t={t} />
          ))}
        </div>
      </section>
    );
  }

  // Line height % = revealedCount / total * 100.
  //   0 cards → 0 % (no line visible)
  //   1 card  → 25 % (matches row 1)
  //   2 cards → 50 %, 3 → 75 %, 4 → 100 %
  const linePercent = (revealedCount / STEP_CARDS.length) * 100;

  // Mobile (< md): line on the left edge, cards stack 1-col with
  // padding-left to clear the line. md+: line in the center gap of
  // the 2-col zigzag grid.
  const lineLeft = isMobile ? "14px" : "50%";
  const lineTransform = isMobile ? "none" : "translateX(-50%)";
  const cardPaddingLeft = isMobile ? 36 : 0;

  return (
    <section className="section-y-lg">
      <div className="container-page">
        <div
          className="relative grid gap-y-6 md:gap-y-10 mx-auto grid-cols-1 md:grid-cols-2"
          style={{
            maxWidth: 960,
            gridTemplateRows: isMobile ? undefined : "repeat(4, auto)",
            columnGap: isMobile ? 0 : "clamp(56px, 8vw, 128px)",
          }}
        >
          {/*
           * Vertical neon line. Mobile: anchored at left:14px so it
           * runs down the left margin of the column. md+: anchored at
           * left:50% so it sits in the wide column gap that separates
           * the zigzag cards. Sits at z-0 below cards (z-1). Grows
           * downward as cards reveal — height tracks revealedCount.
           */}
          <div
            aria-hidden="true"
            className="absolute top-0 pointer-events-none rounded-full transition-all duration-700 ease-out"
            style={{
              left: lineLeft,
              transform: lineTransform,
              width: 3,
              zIndex: 0,
              background: "rgb(var(--brand))",
              boxShadow:
                "0 0 12px rgb(var(--brand) / 0.65), 0 0 28px rgb(var(--brand) / 0.35), 0 0 56px rgb(var(--brand) / 0.18)",
              height: `${linePercent}%`,
            }}
          />
          {/* Glowing dot at the line's leading (bottom) edge. */}
          <div
            aria-hidden="true"
            className="absolute pointer-events-none rounded-full transition-all duration-700 ease-out"
            style={{
              left: lineLeft,
              transform: lineTransform,
              top: `calc(${linePercent}% - 9px)`,
              width: 18,
              height: 18,
              zIndex: 0,
              background: "rgb(var(--brand))",
              boxShadow:
                "0 0 18px rgb(var(--brand)), 0 0 36px rgb(var(--brand) / 0.65), 0 0 64px rgb(var(--brand) / 0.35)",
              opacity: linePercent > 0 ? 1 : 0,
            }}
          />
          {STEP_CARDS.map((card, i) => {
            const cell = STEP_CELLS[i] ?? STEP_CELLS[0]!;
            const revealed = i < revealedCount;
            return (
              <div
                key={card.n}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                className={cx(
                  "relative transition-all duration-500 ease-out",
                  revealed
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8",
                )}
                style={{
                  ...(isMobile
                    ? {}
                    : { gridRow: cell.row, gridColumn: cell.col }),
                  paddingLeft: cardPaddingLeft,
                  zIndex: 1,
                }}
              >
                <StepCardCompact card={card} t={t} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RulesTableSection({ t }: { t: HowItWorksContent }): JSX.Element {
  return (
    <section className="section-y-lg bg-bg-2 border-y border-border">
      <div className="container-page">
        <SectionHead
          title={
            <>
              {t.rules.title.line1}
              <br />
              <span className="text-brand-gradient">{t.rules.title.line2Highlight}</span>
            </>
          }
        />

        {/*
         * Mobile (< md): preserve the 3-column table shape but drop
         * the decorative bars and tighten padding/type so RULE +
         * 1-STEP + 2-STEP all fit at narrow widths. Values wrap to
         * 2 lines for the longer entries ("8% + 5% (two phases)" /
         * "80% (90% with add-on)") instead of overflowing.
         */}
        <div className="md:hidden mt-7 glass-card overflow-hidden">
          <div
            className="grid border-b border-border"
            style={{
              gridTemplateColumns: "1.15fr 1fr 1fr",
              background: "rgb(var(--bg-2) / 0.4)",
            }}
          >
            <div className="px-2.5 py-2 font-mono text-[9.5px] tracking-[0.12em] text-fg-dim">
              RULE
            </div>
            <div className="px-2.5 py-2 border-l border-border font-mono text-[9.5px] tracking-[0.12em] text-fg-dim">
              1-STEP
            </div>
            <div
              className="px-2.5 py-2 border-l border-border font-mono text-[9.5px] tracking-[0.12em] text-brand-emphasis font-bold"
              style={{
                background:
                  "linear-gradient(180deg, rgb(var(--brand) / 0.10), transparent)",
              }}
            >
              2-STEP
            </div>
          </div>
          {RULES_TABLE.map((row, i) => {
            const last = i === RULES_TABLE.length - 1;
            return (
              <div
                key={t.rules.labels[row.key]}
                className={cx("grid", !last && "border-b border-border")}
                style={{ gridTemplateColumns: "1.15fr 1fr 1fr" }}
              >
                <div className="px-2.5 py-2.5 text-[12px] font-semibold leading-tight">
                  {t.rules.labels[row.key]}
                </div>
                <div className="px-2.5 py-2.5 border-l border-border font-mono text-[11px] leading-[1.35]">
                  {ruleText(row.oneStep, t.rules.words)}
                </div>
                <div
                  className="px-2.5 py-2.5 border-l border-border font-mono text-[11px] font-bold text-brand-emphasis leading-[1.35]"
                  style={{
                    background:
                      "linear-gradient(180deg, rgb(var(--brand) / 0.04), transparent)",
                  }}
                >
                  {ruleText(row.twoStep, t.rules.words)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden md:block glass-card mt-9 overflow-hidden">
          {/* Header row */}
          <div
            className="grid border-b border-border"
            style={{ gridTemplateColumns: "1.2fr 1fr 1fr", background: "rgb(var(--bg-2) / 0.4)" }}
          >
            <div className="px-5 py-4 font-mono text-[10.5px] tracking-[0.14em] text-fg-dim">{t.rules.headers.rule}</div>
            <div className="px-5 py-4 border-l border-border">
              <div className="font-mono text-[10.5px] tracking-[0.14em] text-fg-dim">{t.rules.headers.oneStep}</div>
              <div className="font-display text-lg mt-0.5">{t.rules.headers.oneStepSub}</div>
            </div>
            <div className="px-5 py-4 border-l border-border" style={{ background: "linear-gradient(180deg, rgb(var(--brand) / 0.10), transparent)" }}>
              <div className="font-mono text-[10.5px] tracking-[0.14em] text-brand-emphasis">{t.rules.headers.twoStep}</div>
              <div className="font-display text-lg mt-0.5 text-brand-emphasis">{t.rules.headers.twoStepSub}</div>
            </div>
          </div>

          {RULES_TABLE.map((row, i) => {
            const last = i === RULES_TABLE.length - 1;
            return (
              <div
                key={t.rules.labels[row.key]}
                className={cx("grid", !last && "border-b border-border")}
                style={{ gridTemplateColumns: "1.2fr 1fr 1fr" }}
              >
                <div className="px-5 py-4 text-sm font-semibold">{t.rules.labels[row.key]}</div>
                <div className="px-5 py-4 border-l border-border flex items-center gap-3">
                  <span className="font-mono text-[13px] shrink-0" style={{ minWidth: 120 }}>
                    {ruleText(row.oneStep, t.rules.words)}
                  </span>
                  <span className="flex-1 h-[5px] bg-bg-2 rounded-full overflow-hidden">
                    <span
                      className="block h-full bg-fg-muted"
                      style={{ width: `${row.oneStepFill}%` }}
                    />
                  </span>
                </div>
                <div
                  className="px-5 py-4 border-l border-border flex items-center gap-3"
                  style={{ background: "linear-gradient(180deg, rgb(var(--brand) / 0.04), transparent)" }}
                >
                  <span
                    className="font-mono text-[13px] font-bold text-brand-emphasis shrink-0"
                    style={{ minWidth: 120 }}
                  >
                    {ruleText(row.twoStep, t.rules.words)}
                  </span>
                  <span className="flex-1 h-[5px] bg-bg-2 rounded-full overflow-hidden">
                    <span
                      className="block h-full bg-brand-emphasis"
                      style={{ width: `${row.twoStepFill}%`, boxShadow: "0 0 8px rgb(var(--brand))" }}
                    />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Worked example walkthrough — anchored on the $100K · 2-Step
// trader (matches the AccountSelector default and the hero
// preview ticket) so the numbers a prospect sees in the
// configurator above carry through into the lifecycle below.
// Every value is a documented FAQ value or a direct mathematical
// derivation: 8% / 5% targets from "What is the profit target",
// 5% / 10% drawdown from "What is the daily drawdown limit" and
// "What is the maximum drawdown limit", 80% split from "What is
// the profit split", and the example `$5,000 profit → $4,000
// payout` calculation is `0.80 × $5,000`.
interface ExampleStep {
  key: ExampleStepKey;
  n: string;
  keyValue: string;
  keyNum?: number;
}

const EXAMPLE_STEPS: readonly ExampleStep[] = [
  { key: "payFee", n: "01", keyValue: "$499", keyNum: 499 },
  { key: "phase1", n: "02", keyValue: "$108,000", keyNum: 108000 },
  { key: "phase2", n: "03", keyValue: "$105,000", keyNum: 105000 },
  { key: "withdraw", n: "04", keyValue: "$4,000", keyNum: 4000 },
];

function WorkedExample({ t }: { t: HowItWorksContent }): JSX.Element {
  return (
    <section className="section-y-lg bg-bg-2 border-y border-border">
      <div className="container-page">
        <SectionHead
          title={
            <>
              {t.example.title.lead}
              <span className="text-brand-gradient">{t.example.title.highlight}</span>
              {t.example.title.tail}
            </>
          }
          sub={t.example.sub}
        />

        <RevealStagger className="mt-10 md:mt-12 grid gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-4" step={90}>
          {EXAMPLE_STEPS.map((s) => (
            <div key={s.key} className="glass-card glass-card-hover h-full flex flex-col p-4 md:p-6 relative">
              <div className="font-mono text-[9.5px] md:text-[10px] tracking-[0.18em] text-brand-emphasis font-bold">
                {t.example.steps[s.key].phase}
              </div>
              <h3
                className="font-display font-semibold mt-2.5 md:mt-3 text-[17px] md:text-[22px]"
                style={{ letterSpacing: "-0.015em" }}
              >
                {t.example.steps[s.key].title}
              </h3>
              <p className="text-fg-muted text-[12px] md:text-[13px] leading-[1.5] md:leading-[1.55] mt-2.5 md:mt-3 flex-1">{t.example.steps[s.key].body}</p>
              <div className="mt-3 md:mt-4 pt-3 md:pt-3.5 border-t border-border">
                <div className="font-mono text-[9px] md:text-[9.5px] tracking-[0.16em] md:tracking-[0.18em] text-fg-dim uppercase">
                  {t.example.steps[s.key].keyLabel}
                </div>
                <div
                  className="font-display text-brand-gradient mt-1 font-bold leading-none tracking-tight text-[24px] md:text-[30px]"
                  style={{ letterSpacing: "-0.025em" }}
                >
                  {s.keyNum !== undefined ? (
                    <CountUp to={s.keyNum} duration={1600} format={fmtUSD} />
                  ) : (
                    s.keyValue
                  )}
                </div>
              </div>
            </div>
          ))}
        </RevealStagger>

        <p className="mt-8 max-w-[720px] mx-auto text-center text-fg-muted text-[13px] leading-[1.7]">
          {t.example.footnote}
        </p>
      </div>
    </section>
  );
}

// Scaling plan milestones — drawn from the FAQ "How does the
// Scaling Plan work?" + "How much can my account grow?". Cycle
// length (every 4 months), eligibility criteria (+10% net,
// 2 successful payouts, no violations), and the $1M ceiling are
// all documented values. The 5-step ladder below shows the
// canonical FAQ progression starting from a typical funded
// account; the actual starting balance varies by tier purchased.
interface ScaleStep {
  key: ScaleRowKey;
}

const SCALE_LADDER: readonly ScaleStep[] = [
  { key: "funded" },
  { key: "mo4" },
  { key: "mo8" },
  { key: "mo12" },
  { key: "mo20" },
];

function ScalingPlan({ t }: { t: HowItWorksContent }): JSX.Element {
  return (
    <section className="section-y-lg">
      <div className="container-page">
        <SectionHead
          title={
            <>
              {t.scaling.title.lead}
              <span className="text-brand-gradient">{t.scaling.title.highlight}</span>
            </>
          }
          sub={t.scaling.sub}
        />

        <div className="mt-10 md:mt-12 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
          {/* LEFT — milestone ladder */}
          <div className="glass-card p-5 md:p-7">
            <div className="font-mono text-[11px] tracking-[0.18em] text-fg-dim uppercase mb-5">
              {t.scaling.milestonePath}
            </div>
            <div className="flex flex-col">
              {SCALE_LADDER.map((s, i) => {
                const last = i === SCALE_LADDER.length - 1;
                return (
                  <div
                    key={s.key}
                    className={cx(
                      "flex items-baseline justify-between gap-4 py-3.5",
                      !last && "border-b border-border",
                    )}
                  >
                    <div className="flex items-baseline gap-3.5 flex-1 min-w-0">
                      <span className="font-mono text-[11px] tracking-[0.14em] text-brand-emphasis font-bold w-[68px] shrink-0">
                        {t.scaling.ladder[s.key].cycle}
                      </span>
                      <span className="text-[14px] md:text-[15px] truncate">{t.scaling.ladder[s.key].size}</span>
                    </div>
                    <span className="font-mono text-[12px] md:text-[13px] font-bold text-fg shrink-0">
                      {t.scaling.ladder[s.key].delta}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — eligibility criteria */}
          <div
            className="glass-card glass-card-glow p-5 md:p-7"
            style={{
              background:
                "linear-gradient(180deg, rgb(var(--brand) / 0.08) 0%, rgb(var(--brand) / 0.02) 100%)",
            }}
          >
            <div className="font-mono text-[11px] tracking-[0.18em] text-fg-dim uppercase">
              {t.scaling.eligibilityTitle}
            </div>
            <h3
              className="font-display font-semibold mt-4"
              style={{ fontSize: 26, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              {t.scaling.eligibilitySub}
            </h3>
            <div className="mt-5 pt-4 border-t border-border flex flex-col gap-2.5">
              {t.scaling.criteria.map((c) => (
                <div key={c} className="flex items-start gap-2 text-[13px] md:text-[14px]">
                  <span className="text-brand-emphasis mt-0.5" aria-hidden="true">→</span>
                  <span className="text-fg-muted">{c}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-border font-mono text-[10.5px] tracking-[0.06em] text-fg-dim leading-[1.55]">
              {t.scaling.note}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQRedirect({ t }: { t: HowItWorksContent }): JSX.Element {
  // Top 4 questions surface as link-rows. Each row deep-links into
  // the matching article on the platform's FAQ knowledge base
  // (`trade.flex-fund.com/faq/...`); the apex never serves FAQ
  // content. Article paths are pinned in `FAQ_ITEMS[i].path`.
  const previews = FAQ_ITEMS.slice(0, 4);
  return (
    <section className="section-y-lg bg-bg-2 border-y border-border">
      <div className="container-page">
        <div className="flex justify-between items-end gap-6 flex-wrap mb-9">
          <SectionHead
            title={
              <>
                {t.faq.title.lead}
                <span className="text-brand-gradient">{t.faq.title.highlight}</span>
                {t.faq.title.tail}
              </>
            }
            sub={t.faq.sub}
          />
          <Button variant="primary" href={FAQ_BASE_URL}>
            {t.faq.browseAll} <ArrowRight />
          </Button>
        </div>

        <div className="glass-card overflow-hidden">
          {previews.map((q, i) => {
            const last = i === previews.length - 1;
            return (
              <a
                key={q.key}
                href={`${FAQ_BASE_URL}/${q.path}`}
                className={cx(
                  "flex justify-between items-center gap-4 px-6 py-5 transition-colors hover:bg-card-hover",
                  !last && "border-b border-border",
                )}
              >
                <div>
                  <div className="font-mono text-[10.5px] text-fg-dim tracking-[0.12em]">
                    {t.faq.items[q.key].category}
                  </div>
                  <div className="font-semibold text-[15px] mt-1">{t.faq.items[q.key].q}</div>
                </div>
                <span className="text-fg-muted shrink-0">
                  <ChevronRight />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function HowItWorksPage(): JSX.Element {
  const t = howItWorksFor(useLocale());
  usePageMeta({
    title: t.meta.title,
    description: t.meta.description,
  });
  useBreadcrumbStructuredData([
    { name: "Home", url: "/" },
    { name: "How It Works", url: "/how-it-works" },
  ]);
  return (
    <>
      <GridBackdrop />
      <div className="animate-page-in relative z-[1]">
        <Hero t={t} />
        <StepCards t={t} />
        <RulesTableSection t={t} />
        <AccountSelector />
        <WorkedExample t={t} />
        <ScalingPlan t={t} />
        <FAQRedirect t={t} />
      </div>
    </>
  );
}
